import { attach, combine, createEvent, createStore, sample, split } from 'effector'
import { produce } from 'immer'
import { nanoid } from 'nanoid'
import { debounce } from 'patronum'

import * as chat from '@/entities/chat'
import { $i18n } from '@/shared/lib/i18n'
import { formatDateTime } from '@/shared/lib/time'
import { showErrorNotificationFx } from '@/shared/ui'

import { chatapi, type Message, type MessagePublishRequest } from '../api'
import { MessageHideDialog } from '../dialog/message-hide'
import { MessageReportDialog } from '../dialog/message-report'
import { getMessageLength, isMessageContainsExternalLinks } from '../lib'
import { addChannelItem } from './lib'
import * as channelModel from './model.channel'
import * as connModel from './model.conn'
import * as settingModel from './model.setting'
import * as userModel from './model.user'
import type { MessagePublish } from './types'

export const $chatMessageText = createStore<Record<string, string>>({})
export const $chatMessageReply = createStore<Record<string, Message | null>>({})

export const $messageText = combine(
    $chatMessageText,
    channelModel.$chatChannel,
    (messages, channel) => messages[channel] || ''
)

export const $messageReply = combine(
    $chatMessageReply,
    channelModel.$chatChannel,
    (messages, channel) => messages[channel] || null
)

export const messageInputEnterPressed = createEvent()
export const messageInputTextChanged = createEvent<string>()

export const messageTextChanged = createEvent<string>()

export const messageReplyClicked = createEvent<Message>()
export const messageReplyCancelClicked = createEvent()

export const messageSend = createEvent<MessagePublish>()
export const messageReport = createEvent<{ message_id: string }>()
export const messageDelete = createEvent<{
    message_id: string
    author_id: string
}>()

export const messagePublishFx = attach({ effect: chatapi.messagePublish })

// --- Emoji ---
export const emojiSelected = createEvent<string>()

sample({
    clock: emojiSelected,
    source: $messageText,
    filter: userModel.$canSendMessages,
    fn: (message, emoji) => `${message}${emoji}`,
    target: messageInputTextChanged,
})

// --- Input ---

sample({
    clock: messageInputTextChanged,
    source: {
        userInfo: chat.$userInfo,
        limits: settingModel.$messageLimits,
    },
    filter: ({ userInfo, limits }, message) => {
        // admins and moderators can send longer messages
        const maxMessageSize = chat.isPrivilegedUser(userInfo) ? limits.admin : limits.user
        return getMessageLength(message) <= maxMessageSize
    },
    fn: (_userInfo, message) => message,
    target: messageTextChanged,
})

sample({
    clock: messageTextChanged,
    source: {
        channel: channelModel.$chatChannel,
        textByChannel: $chatMessageText,
    },
    fn: ({ channel, textByChannel }, text) => ({
        ...textByChannel,
        [channel]: text,
    }),
    target: $chatMessageText,
})

// -- Send message ---

sample({
    clock: messageInputEnterPressed,
    source: {
        message: $messageText.map((message) => message.trim()),
        channel: channelModel.$chatChannel,
        userInfo: chat.$userInfo,
        replyMessage: $messageReply,
    },
    filter: (data): data is MessagePublish => data.message.length > 0 && data.userInfo !== null,
    // attach optimistic id
    fn: (event: MessagePublish): MessagePublish => ({ ...event, optimisticId: nanoid() }),
    target: messageSend,
})

export const messageSendStatus = split(messageSend, {
    userMuted: (event) => chat.isUserMuted(event.userInfo.mute),
    containsExternalLinks: (event) => isMessageContainsExternalLinks(event.message),
    allowed: () => true,
})

sample({
    clock: messageSendStatus.userMuted,
    source: $i18n,
    fn: ({ i18n, lang }, { userInfo }) => {
        const message = userInfo.mute?.expire_time
            ? i18n.CHAT_ADMIN__BAN_TIMER.replace('{string01}', formatDateTime(userInfo.mute.expire_time, lang))
            : i18n.CHAT_ADMIN__BAN_FOREVER

        return { message }
    },
    target: showErrorNotificationFx,
})

sample({
    clock: messageSendStatus.containsExternalLinks,
    source: $i18n,
    fn: ({ i18n }) => ({ message: i18n.CHAT_ADMIN__LINKS_PROHIBITED }),
    target: showErrorNotificationFx,
})

// add optimistic message to a channel
connModel.$chatItems.on(messageSendStatus.allowed, (items, event) =>
    addChannelItem(event.channel, items, {
        type: 'message',
        payload: {
            id: event.optimisticId,
            user_id: event.userInfo.user_id,
            data: event.message,
            create_time: new Date(),
            optimistic_id: event.optimisticId,
            reply_message_id: event.replyMessage?.id,
            reply_user_id: event.replyMessage?.user_id,
        },
    })
)

sample({
    clock: messageSendStatus.allowed,
    fn: (event): MessagePublishRequest => ({
        channel: event.channel,
        message: event.message,
        optimistic_id: event.optimisticId,
        reply_message_id: event.replyMessage?.id,
    }),
    target: messagePublishFx,
})

sample({
    clock: messagePublishFx.failData,
    target: showErrorNotificationFx,
})

// reset message input after message publish
sample({
    clock: messageSendStatus.allowed,
    target: messageTextChanged.prepend(() => ''),
})

// -- Report Message --

sample({
    clock: messageReport,
    source: {
        channel: channelModel.$chatChannel,
    },
    fn: ({ channel }, { message_id }) => ({
        message_id,
        channel,
    }),
    target: MessageReportDialog.model.showFx,
})

// -- Delete Message --

sample({
    clock: messageDelete,
    source: {
        channel: channelModel.$chatChannel,
    },
    fn: ({ channel }, { message_id, author_id }) => ({
        channel,
        author_id,
        message_id,
    }),
    target: MessageHideDialog.model.showFx,
})

// -- Reply Message --

sample({
    clock: messageReplyClicked,
    source: {
        channel: channelModel.$chatChannel,
        reply: $chatMessageReply,
    },
    fn: ({ channel, reply }, message) => ({
        ...reply,
        [channel]: message,
    }),
    target: $chatMessageReply,
})

sample({
    clock: [messageSendStatus.allowed, messageReplyCancelClicked],
    source: {
        channel: channelModel.$chatChannel,
        reply: $chatMessageReply,
    },
    fn: ({ channel, reply }) => ({
        ...reply,
        [channel]: null,
    }),
    target: $chatMessageReply,
})

// --- Highlight Message ---

export const $messageHighlightId = createStore<string | null>(null)
export const messageHighlight = createEvent<{ messageId: string }>()

sample({
    clock: messageHighlight,
    fn: ({ messageId }) => messageId,
    target: $messageHighlightId,
})

// reset highlight after N seconds
sample({
    clock: debounce({ source: messageHighlight, timeout: 2000 }),
    fn: () => null,
    target: $messageHighlightId,
})

// -- Hide all user's messages in every channel

sample({
    clock: connModel.messageHiddenAll,
    source: connModel.$chatItems,
    fn: (chatItems, { user_id }) =>
        produce(chatItems, (chatItems) => {
            for (const [channel, items] of Object.entries(chatItems)) {
                chatItems[channel] = items.filter((item) => {
                    if (item.type === 'message' || item.type === 'message_group') {
                        return item.payload.user_id !== user_id
                    }
                    return true
                })
            }
        }),
    target: connModel.$chatItems,
})
